import { Button, LinkButton } from '@portal/components'
import { Container } from 'components/layout'
import { FallbackProps } from 'react-error-boundary'

import { CODE_TO_HEADER, CODE_TO_MESSAGE, PortalError } from './error'
import ErrorHeaderLogo from './ErrorHeaderLogo'

/**
 * A fallback UI to display when an error occurs in the app.
 *
 * If the error is a PortalError, it will display relevant information based on
 * the error code. Otherwise, the messaging will be pulled from the error, itself,
 * if available.
 */
export default function ErrorFallback({
  error,
  resetErrorBoundary,
}: FallbackProps) {
  /**
   * A callback to reset the error boundary, which will attempt to retry the
   * render
   */
  const handleResetBoundary = () => resetErrorBoundary()

  /**
   * The header to display based on the error code, if available.
   *
   * Note that RelayNetworkLayer does not support throwing custom errors when it aborts a fetch
   * after our retry limit is reached, so we check the message string for this scenario
   */
  const renderHeader = () => {
    if (error instanceof PortalError) {
      return CODE_TO_HEADER[error.code] || FALLBACK_HEADER
    }

    const message = error?.message
    if (message === CODE_TO_MESSAGE['RELAY_FETCH_FAILED']) {
      return CODE_TO_HEADER['RELAY_FETCH_FAILED'] || FALLBACK_HEADER
    }

    return FALLBACK_HEADER
  }

  /**
   * The message to display based on the error code, if available
   */
  const renderMessage = () => {
    if (error instanceof PortalError) {
      return CODE_TO_MESSAGE[error.code] || FALLBACK_MESSAGE
    } else {
      return error?.message || FALLBACK_MESSAGE
    }
  }

  return (
    <Container className="flex h-screen flex-col items-center justify-center space-y-8">
      <ErrorHeaderLogo />

      <div className="text-center md:max-w-md">
        <h1
          className="mb-4 font-kairos text-2xl leading-7 text-foreground-normal"
          data-testid="errorHeader"
        >
          {renderHeader()}
        </h1>
        <p
          className="text-base leading-5 text-foreground-subtle"
          data-testid="errorMessage"
        >
          {renderMessage()}
        </p>
      </div>

      <div
        className="flex h-10 justify-center space-x-2"
        data-testid="errorActions"
      >
        <LinkButton
          to="/"
          onClick={handleResetBoundary}
          data-testid="errorHomeButton"
        >
          Go home
        </LinkButton>
        <Button
          variant="primary"
          onClick={handleResetBoundary}
          data-testid="errorTryAgainButton"
        >
          Try again
        </Button>
      </div>
    </Container>
  )
}

const FALLBACK_HEADER = 'Something went wrong'
const FALLBACK_MESSAGE = 'An unexpected error occurred. Please try again later.'
